/**
 * Beginner mode: 'your card' intro, shown each round your card is new, before the targets
 * intro. Your card flips off your seat in the play field (or, already face up after a war, just
 * moves off it; the table stays in view until it lands, then the backdrop fades in) and its Power,
 * pointed out on the card by an arrow from its box, with example dice. Next swaps it for the Kill
 * Quest (one subject at a time, so the text can be big); Next again sends the card back to your
 * seat. Next is never greyed out: pressed during the flip it goes straight to the Power.
 * Enemy cards stay face down on the table until the targets intro (see `GameTable`).
 *
 * Wide screens: card centre-left, the round's rule and the explanations to its right.
 * Phones: the card fills the screen and the explanation boxes sit over the character
 * (same grid cell as the card, see styles.css).
 */
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { t, type CardDef, type PublicPlayer } from '@kadice/shared';
import { KillExample, PowerExampleDice, PowerLine } from '../beginner';
import { cardImage, killerName } from '../cards';
import { tx } from '../text';

const FLIP_MS = 900;
const FLY_MS = 800;
/** When the Power shows by itself (Next shows it sooner). */
const POWER_MS = 1_400;
/** Steps: 0 the flip, 1 the Power, 2 the Kill Quest. */
const LAST_STEP = 2;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface Line {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Arrow from an explanation box to its spot on the card (viewport pixels). Box beside the card
 * (wide screens): from its left edge to the spot's right edge. Box over the card (phones): from
 * its top or bottom edge to the spot's facing edge; `aim` is how far down the spot to point at
 * when the box covers its top (the Kill Quest dice sit in the lower part of the card).
 */
function arrowLine(box: DOMRect, spot: DOMRect, aim: number): Line {
  const gap = 6;
  if (box.left >= spot.right - 1) {
    return { x1: box.left - gap, y1: box.top + 20, x2: spot.right + gap, y2: spot.top + spot.height / 2 };
  }
  const x = spot.left + spot.width / 2;
  if (box.top >= spot.bottom) return { x1: x, y1: box.top - gap, x2: x, y2: spot.bottom + gap };
  const target = spot.top + spot.height * aim;
  return { x1: x, y1: box.bottom + gap, x2: x, y2: Math.max(target, box.bottom + 44) };
}

function useArrow(on: boolean, box: RefObject<HTMLElement | null>, spot: RefObject<HTMLElement | null>, aim: number) {
  const [line, setLine] = useState<Line | null>(null);
  useEffect(() => {
    const measure = () => {
      const b = box.current?.getBoundingClientRect();
      const s = spot.current?.getBoundingClientRect();
      setLine(on && b && s ? arrowLine(b, s, aim) : null);
    };
    // Measured once laid out, then again whenever a box finishes sliding in (a transition) or
    // the window changes.
    const frame = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    document.addEventListener('transitionend', measure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
      document.removeEventListener('transitionend', measure);
    };
  }, [on, box, spot, aim]);
  return line;
}

/** Your card on the table (your seat): where the intro card flies from and back to. */
const seatCard = (id: number) => document.querySelector<HTMLElement>(`.table [data-card-of="${id}"] .card`);

export function CardIntro({
  self,
  card,
  faceUp,
  onDone,
}: {
  self: PublicPlayer;
  card: CardDef;
  /** Already face up on the table (flipped by a war): it moves off the seat without flipping. */
  faceUp: boolean;
  onDone: () => void;
}) {
  const flipRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const [arriving, setArriving] = useState(() => !reducedMotion());
  // Flown in (not faded in): set once, so the fade-in never replays when the flight ends.
  const [flown] = useState(() => !reducedMotion());
  const [leaving, setLeaving] = useState(false);
  const powerBox = useRef<HTMLElement>(null);
  const questBox = useRef<HTMLElement>(null);
  const powerSpot = useRef<HTMLSpanElement>(null);
  const questSpot = useRef<HTMLSpanElement>(null);
  const powerArrow = useArrow(step === 1, powerBox, powerSpot, 0);
  const questArrow = useArrow(step === 2, questBox, questSpot, 0.15);

  // Flip in from your deck on the table (or just appear, if it can't be found).
  useLayoutEffect(() => {
    const el = flipRef.current;
    const deck = seatCard(self.id);
    const timers = [
      setTimeout(() => setStep((s) => Math.max(s, 1)), POWER_MS),
      setTimeout(() => setArriving(false), FLIP_MS),
    ];
    if (!el || reducedMotion()) return () => timers.forEach(clearTimeout);
    const to = el.getBoundingClientRect();
    const from = deck?.getBoundingClientRect();
    const flip = faceUp ? '' : ' rotateY(180deg)';
    const start = from
      ? `translate(${from.left + from.width / 2 - (to.left + to.width / 2)}px, ${from.top + from.height / 2 - (to.top + to.height / 2)}px) scale(${from.width / to.width})${flip}`
      : `scale(0.4)${flip}`;
    const anim = el.animate([{ transform: start }, { transform: 'none' }], {
      duration: FLIP_MS,
      easing: 'cubic-bezier(0.3, 0, 0.2, 1)',
    });
    return () => {
      timers.forEach(clearTimeout);
      anim.cancel();
    };
    // Mounted per intro (keyed), so this runs once.
  }, [self.id, faceUp]);

  // The card on the table is the one in the intro: the table's stays out of sight until Next
  // has put the intro's back on it (so there is only ever one card to see).
  useLayoutEffect(() => {
    const spot = seatCard(self.id);
    if (spot) spot.style.visibility = 'hidden';
    return () => {
      if (spot) spot.style.visibility = '';
    };
  }, [self.id]);

  const next = () => {
    if (leaving) return;
    if (step < LAST_STEP) {
      setArriving(false);
      setStep(step + 1);
      return;
    }
    setLeaving(true);
    const el = flipRef.current;
    const target = seatCard(self.id);
    if (!el || !target || reducedMotion()) return onDone();
    const from = el.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + to.height / 2 - (from.top + from.height / 2);
    el.animate([{ transform: 'none' }, { transform: `translate(${dx}px, ${dy}px) scale(${to.width / from.width})` }], {
      duration: FLY_MS,
      easing: 'cubic-bezier(0.5, 0, 0.2, 1)',
      fill: 'forwards',
    }).onfinish = onDone;
  };

  return (
    <div
      className={`card-intro${flown ? ' flown' : ''}${arriving ? ' arriving' : ''}${leaving ? ' leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={t('cardIntro.label')}
    >
      <div className="card-intro-stage">
        <div className="card-flip" ref={flipRef}>
          <img src={cardImage(self.color, 'back')} alt="" className="card back" />
          <div className="card-flip-front">
            <img src={cardImage(self.color, self.card!)} alt={card.name} className="card" />
            <span ref={powerSpot} className={`card-spot power${step === 1 ? ' on' : ''}`} aria-hidden />
            <span ref={questSpot} className={`card-spot quest${step === 2 ? ' on' : ''}`} aria-hidden />
          </div>
        </div>
      </div>

      <div className="card-intro-head card-intro-fade">
        <h2>{t('cardIntro.title')}</h2>
        <p className="card-intro-sub">{t('cardIntro.sub')}</p>
      </div>

      <div className="card-intro-steps card-intro-fade">
        <section ref={powerBox} className={`card-intro-step power${step === 1 ? ' on' : ''}`}>
          <h3>{t('cardIntro.power')}</h3>
          <p>{t('cardIntro.powerBody')}</p>
          <PowerLine card={card} className="card-intro-this power-line" />
          <PowerExampleDice power={card.power} />
        </section>

        <section ref={questBox} className={`card-intro-step quest${step === 2 ? ' on' : ''}`}>
          <h3>{t('cardIntro.quest')}</h3>
          <p>{t('cardIntro.questBody')}</p>
          <p className="card-intro-this">
            {tx('cardIntro.questThis', { card: <strong>{card.name}</strong>, killer: killerName(card) })}
          </p>
          <KillExample card={card} />
        </section>
      </div>

      <svg className="card-intro-arrows card-intro-fade" aria-hidden>
        <defs>
          {(['power', 'quest'] as const).map((k) => (
            <marker
              key={k}
              id={`intro-head-${k}`}
              className={k}
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="4"
              markerHeight="4"
              orient="auto-start-reverse"
            >
              <path d="M0 0 L10 5 L0 10 z" />
            </marker>
          ))}
        </defs>
        {powerArrow && <line className="power" {...powerArrow} markerEnd="url(#intro-head-power)" />}
        {questArrow && <line className="quest" {...questArrow} markerEnd="url(#intro-head-quest)" />}
      </svg>

      <button className="primary intro-next card-intro-fade" onClick={next}>
        {t('common.next')}
      </button>
    </div>
  );
}
